import React, { useCallback } from 'react';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { PromotionMetricCards } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/PromotionMetricCards';
import { PromotionCountDonutChart } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionCountDonutChart';
import { PromotionsByWeekTable } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionsByWeekTable';
import PromotionsDealTypeColumnChart from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionsDealTypeColumnChart';
import { PromotionsProductTable } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionsProductTable';
import PromotionsTopEntityChart from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionsTopEntityChart';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useMetricFormatter } from 'src/utils/Hooks';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

/**
 * Promotions Summary Page under the Prices tab.
 */
const PromotionsSummaryPage = () => {
  const { isProductEntity } = useProductEntity();
  const formatMetric = useMetricFormatter();

  const renderView = useCallback(() => {
    if (isProductEntity) {
      return (
        <BasePageContainer>
          <Flex gap="lg" flexDirection="column">
            <CommonSummaryInfoSubtitle title="Promotion Count" />
            <PromotionMetricCards />
          </Flex>
          <Flex marginTop="80px" gap={BEACON_CHART_SPACING} flexDirection="column">
            <PromotionsDealTypeColumnChart />
            <PromotionsByWeekTable />
          </Flex>
        </BasePageContainer>
      );
    } else {
      return (
        <BasePageContainer>
          <Flex gap={BEACON_CHART_SPACING} flexDirection="column">
            <ChartContextProvider
              group={CHART_CONTEXT_GROUPS.BEACON_PROMOTION_COUNT.name}
              chartName={CHART_NAMES.BEACON_PROMOTION_COUNT_SPLINE}
            >
              <Flex gap="lg" flexDirection="column">
                <CommonSummaryInfoSubtitle title="Promotion Count" />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <PromotionCountDonutChart />
                  <div
                    style={{
                      width: '624px'
                    }}
                  >
                    <BeaconComparableSplineChart
                      title="Promotion Count"
                      aggregationFunctionForTotal="sum"
                      beaconChartWithLegendOverride={(
                        props,
                        { primaryTotal, secondaryTotal, formattedPercentChange, percentChange }
                      ) => {
                        return {
                          ...props,
                          primaryLegendProps: {
                            ...props.primaryLegendProps,
                            metric: formatMetric(primaryTotal, METRICTYPE.VOLUME),
                            change: formattedPercentChange,
                            direction: getDirection(percentChange)
                          },
                          comparisonLegendProps: {
                            ...props.comparisonLegendProps,
                            metric: formatMetric(secondaryTotal, METRICTYPE.VOLUME)
                          }
                        };
                      }}
                      indexName={BEACON_INDEX_NAMES.PROMOTIONS}
                      fieldName="stacklineSku"
                      showTitleSummary={false}
                      splineChartLoadingProps={{
                        width: 543
                      }}
                      chartOptionsOverride={{
                        chart: {
                          height: 300
                        }
                      }}
                      requestBuilder={(builder) => {
                        builder.addConditionRangeFilter('retailSales', 1);
                        return builder;
                      }}
                    />
                  </div>
                </div>
              </Flex>
            </ChartContextProvider>
            <ChartContextProvider
              group={CHART_CONTEXT_GROUPS.BEACON_PROMOTION_TOP_ENTITY.name}
              chartName={CHART_NAMES.BEACON_PROMOTION_TOP_ENTITY_COLUMN}
            >
              <PromotionsTopEntityChart />
            </ChartContextProvider>
            <ChartContextProvider
              group={CHART_CONTEXT_GROUPS.BEACON_PROMOTION_DEAL_TYPE.name}
              chartName={CHART_NAMES.BEACON_PROMOTION_DEAL_TYPE_COLUMN}
            >
              <PromotionsDealTypeColumnChart />
            </ChartContextProvider>
            <PromotionsProductTable />
          </Flex>
        </BasePageContainer>
      );
    }
  }, [isProductEntity, formatMetric]);

  return renderView();
};

export default PromotionsSummaryPage;
